import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ulid } from 'ulid';
import { format } from 'date-fns';
import type { RootStore } from '.';
import type { CardDateEntry, CardDateType } from '@creative-kit/shared';
import { parseNaturalLanguageDate } from 'utils/date-parsing';

/**
 * Represents a staged date entry (not yet saved to Firestore)
 * Uses a type alias instead of interface because CardDateEntry is a discriminated union
 */
export type StagedDateEntry = CardDateEntry & {
  isNew?: boolean; // true if this entry doesn't exist in Firestore yet
  isModified?: boolean; // true if this entry was modified from Firestore version
};

/**
 * Store for managing the card date assignment dialog state
 * Uses a panel-based architecture with staged changes
 */
export class UiCardDateStore {
  private rootStore: RootStore;

  // Dialog visibility
  isDialogOpen = false;
  targetCardIds: string[] = [];

  // Panel state - which type panel is currently open (null = none selected)
  selectedPanel: string | null = null; // type string or custom entry id

  // Staged entries - changes not yet committed to Firestore
  // Keyed by type for standard types, or by id for custom entries
  stagedEntries: Map<string, StagedDateEntry> = new Map();

  // Original entries from Firestore (for comparison/cancel)
  originalEntries: CardDateEntry[] = [];

  // Panel form state
  startInput = '';
  endInput = '';
  isTentative = false;
  tentativeNote = '';
  customLabel = ''; // for new custom entries

  // Adding new custom entry mode
  isAddingCustom = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      isDialogOpen: observable,
      targetCardIds: observable,
      selectedPanel: observable,
      stagedEntries: observable,
      originalEntries: observable,
      startInput: observable,
      endInput: observable,
      isTentative: observable,
      tentativeNote: observable,
      customLabel: observable,
      isAddingCustom: observable,
      isBatchMode: computed,
      targetCardCount: computed,
      stagedEntriesArray: computed,
      customEntries: computed,
      hasUnsavedChanges: computed,
      currentPanelEntry: computed,
      openDialog: action.bound,
      closeDialog: action.bound,
      selectPanel: action.bound,
      setStartInput: action.bound,
      setEndInput: action.bound,
      setTentative: action.bound,
      setTentativeNote: action.bound,
      setCustomLabel: action.bound,
      startAddingCustom: action.bound,
      cancelAddingCustom: action.bound,
      savePanelEntry: action.bound,
      resetPanelEntry: action.bound,
      deleteStagedEntry: action.bound,
      saveAndClose: action.bound,
      cancelDialog: action.bound,
      hasEntryForType: action.bound,
    });
  }

  get isBatchMode() {
    return this.targetCardIds.length > 1;
  }

  get targetCardCount() {
    return this.targetCardIds.length;
  }

  /** Get staged entries as array */
  get stagedEntriesArray(): StagedDateEntry[] {
    return Array.from(this.stagedEntries.values());
  }

  /** Get custom entries (type === 'custom') */
  get customEntries(): StagedDateEntry[] {
    return this.stagedEntriesArray.filter((e) => e.type === 'custom');
  }

  /** Check if there are unsaved changes */
  get hasUnsavedChanges(): boolean {
    return this.stagedEntriesArray.some((e) => e.isNew || e.isModified);
  }

  /** Get the entry for the currently selected panel */
  get currentPanelEntry(): StagedDateEntry | null {
    if (!this.selectedPanel) return null;
    return this.stagedEntries.get(this.selectedPanel) ?? null;
  }

  /** Check if a standard type has a staged entry */
  hasEntryForType(type: CardDateType): boolean {
    return this.stagedEntriesArray.some((e) => e.type === type && type !== 'custom');
  }

  /** Get the key for a standard type (the type itself) or custom entry (the id) */
  private getEntryKey(entry: CardDateEntry): string {
    return entry.type === 'custom' ? entry.id : entry.type;
  }

  /**
   * Open the dialog for one or more cards
   */
  openDialog(cardIds: string | string[], preselectedType?: CardDateType | string) {
    const ids = Array.isArray(cardIds) ? cardIds : [cardIds];
    this.targetCardIds = ids;

    // Load existing dates from the first card (for single card mode)
    if (ids.length === 1) {
      const card = this.rootStore.cardsStore.cards[ids[0]];
      const existingDates = card?.data.dates ?? [];
      this.originalEntries = [...existingDates];

      // Populate staged entries from existing dates
      this.stagedEntries.clear();
      for (const entry of existingDates) {
        const key = this.getEntryKey(entry);
        this.stagedEntries.set(key, { ...entry, isNew: false, isModified: false });
      }
    } else {
      // Batch mode - start with empty staged entries
      this.originalEntries = [];
      this.stagedEntries.clear();
    }

    // Pre-select panel if specified
    if (preselectedType) {
      this.selectPanel(preselectedType);
    } else {
      this.selectedPanel = null;
      this.resetFormState();
    }

    this.isAddingCustom = false;
    this.isDialogOpen = true;
  }

  /**
   * Close the dialog without saving
   */
  closeDialog() {
    this.isDialogOpen = false;
    this.targetCardIds = [];
    this.selectedPanel = null;
    this.stagedEntries.clear();
    this.originalEntries = [];
    this.resetFormState();
    this.isAddingCustom = false;
  }

  /**
   * Select a panel (type or custom entry id)
   */
  selectPanel(panelKey: string) {
    this.selectedPanel = panelKey;
    this.isAddingCustom = false;

    // Load form state from staged entry if it exists
    const entry = this.stagedEntries.get(panelKey);
    if (entry) {
      // Validate dates are valid Date objects before formatting
      const isValidDate = (date: unknown): date is Date => {
        return date instanceof Date && !isNaN(date.getTime());
      };

      this.startInput =
        entry.startDate && isValidDate(entry.startDate)
          ? format(entry.startDate, entry.startHasTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy')
          : '';
      this.endInput =
        entry.endDate && isValidDate(entry.endDate)
          ? format(entry.endDate, entry.endHasTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy')
          : '';
      this.isTentative = entry.isTentative;
      this.tentativeNote = entry.tentativeNote ?? '';
      this.customLabel = entry.customLabel ?? '';
    } else {
      this.resetFormState();
    }
  }

  private resetFormState() {
    this.startInput = '';
    this.endInput = '';
    this.isTentative = false;
    this.tentativeNote = '';
    this.customLabel = '';
  }

  setStartInput(text: string) {
    this.startInput = text;
  }

  setEndInput(text: string) {
    this.endInput = text;
  }

  setTentative(value: boolean) {
    this.isTentative = value;
    if (!value) {
      this.tentativeNote = '';
    }
  }

  setTentativeNote(note: string) {
    this.tentativeNote = note;
  }

  setCustomLabel(label: string) {
    this.customLabel = label;
  }

  /**
   * Start adding a new custom entry
   */
  startAddingCustom() {
    this.isAddingCustom = true;
    this.selectedPanel = null;
    this.resetFormState();
  }

  /**
   * Cancel adding custom entry
   */
  cancelAddingCustom() {
    this.isAddingCustom = false;
    this.resetFormState();
  }

  /**
   * Save the current panel's form to staged entries AND persist to Firestore immediately
   */
  async savePanelEntry() {
    const parsedStart = this.startInput ? parseNaturalLanguageDate(this.startInput) : null;
    const parsedEnd = this.endInput ? parseNaturalLanguageDate(this.endInput) : null;

    // Must have at least one date
    if (!parsedStart && !parsedEnd) return;

    let entryKey: string;
    let entryType: CardDateType;
    let isNew = false;

    if (this.isAddingCustom) {
      // Creating new custom entry
      entryKey = ulid();
      entryType = 'custom';
      isNew = true;
    } else if (this.selectedPanel) {
      // Editing existing panel
      entryKey = this.selectedPanel;
      const existing = this.stagedEntries.get(entryKey);
      if (existing) {
        entryType = existing.type;
      } else {
        // New standard type entry
        entryType = this.selectedPanel as CardDateType;
        isNew = true;
      }
    } else {
      return;
    }

    const baseEntry = {
      id: this.isAddingCustom ? entryKey : this.stagedEntries.get(entryKey)?.id ?? ulid(),
      startDate: parsedStart?.date,
      endDate: parsedEnd?.date,
      startHasTime: parsedStart?.hasTime ?? false,
      endHasTime: parsedEnd?.hasTime ?? false,
      isTentative: this.isTentative,
      tentativeNote:
        this.isTentative && this.tentativeNote.trim() ? this.tentativeNote.trim() : undefined,
      isNew,
      isModified: !isNew,
    };

    const entry: StagedDateEntry =
      entryType === 'custom'
        ? {
            ...baseEntry,
            type: 'custom',
            customLabel: this.customLabel.trim() || 'Custom Date',
          }
        : {
            ...baseEntry,
            type: entryType,
          };

    runInAction(() => {
      this.stagedEntries.set(this.isAddingCustom ? entry.id : entryKey, entry);

      if (this.isAddingCustom) {
        // Select the newly created custom entry
        this.selectedPanel = entry.id;
        this.isAddingCustom = false;
      }
    });

    // Immediately persist to Firestore
    await this.persistStagedEntries();
  }

  /**
   * Persist all staged entries to Firestore (without closing dialog)
   */
  private async persistStagedEntries() {
    // Early return if dialog was closed during async operation
    if (!this.isDialogOpen) return;

    const entries = this.stagedEntriesArray.map((e) => {
      // Remove staging metadata before saving
      const { isNew, isModified, ...cleanEntry } = e;
      return cleanEntry as CardDateEntry;
    });

    const { sessionCardActions, uiSnackbarStore } = this.rootStore;

    try {
      if (this.isBatchMode) {
        await sessionCardActions.batchSetCardDates(this.targetCardIds, entries);
        uiSnackbarStore.setSnackbarDetails({
          description: `Updated dates for ${this.targetCardIds.length} cards`,
        });
      } else {
        await sessionCardActions.setCardDates(this.targetCardIds[0], entries);
        uiSnackbarStore.setSnackbarDetails({ description: 'Date saved' });
      }

      // Mark all entries as no longer new/modified since they're persisted
      // Check dialog is still open before updating state
      if (this.isDialogOpen) {
        runInAction(() => {
          for (const [key, entry] of this.stagedEntries) {
            this.stagedEntries.set(key, { ...entry, isNew: false, isModified: false });
          }
        });
      }
    } catch (error) {
      // Only show error if dialog is still open
      if (this.isDialogOpen) {
        console.error('Failed to save dates:', error);
        uiSnackbarStore.setSnackbarDetails({
          description: 'Unable to save dates. Please try again or refresh the page.',
        });
      }
    }
  }

  /**
   * Reset/clear the current panel's entry and persist immediately
   */
  async resetPanelEntry() {
    if (!this.selectedPanel) return;

    runInAction(() => {
      this.stagedEntries.delete(this.selectedPanel!);
      this.resetFormState();
    });

    // Persist the deletion to Firestore
    await this.persistStagedEntries();
  }

  /**
   * Delete a staged entry (from chip X button) and persist immediately
   */
  async deleteStagedEntry(key: string) {
    runInAction(() => {
      this.stagedEntries.delete(key);
      if (this.selectedPanel === key) {
        this.selectedPanel = null;
        this.resetFormState();
      }
    });

    // Persist the deletion to Firestore
    await this.persistStagedEntries();
  }

  /**
   * Save all staged entries to Firestore and close dialog
   */
  async saveAndClose() {
    await this.persistStagedEntries();
    this.closeDialog();
  }

  /**
   * Cancel all changes and close dialog
   */
  cancelDialog() {
    this.closeDialog();
  }
}
